import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../api/client'
import type { Instrument } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { type SensorState, useSensorDetails } from '../lib/sensorDetails'
import { NO_TYPE } from '../lib/sensorMap'
import { hiddenAt, showAllChannels, useHiddenChannels, withoutHidden } from '../lib/hiddenChannels'
import {
  type ChartGroup, chartGroups, defaultSelection, exportColumnsFor,
  instrumentLabel, mergeFamilies, parametersFor, splitSections,
} from '../lib/instruments'
import { absentMessage, absentNames, drawsNothing, emptyWindowMessage, truncationMessage } from '../lib/format'
import { freshness } from '../lib/freshness'
import { zoomedCharts } from '../lib/zoom'
import { type Pinning, useChartPins } from '../lib/useChartPins'
import { instrumentChartKey } from '../lib/chartFormat'
import { ChartFrame } from './ChartFrame'
import { ExportPanel } from './ExportPanel'
import { FamilySection, OtherSection } from './ChannelTable'
import { KindNotes } from './KindNotes'
import { TimeSeriesChart } from './TimeSeriesChart'

/** Re-asked on the latest-readings panel's cadence. Each block is that
 *  panel for one ticked sensor: 8-90 channel probes each, instead of the
 *  station's 1,200 (spec §8.4). */
const REFRESH_MS = 30_000

interface Props {
  code: string
  /** Every instrument at the station, for the names of the ticked ones
   *  before their readings arrive. */
  instruments: Instrument[]
  /** The ticked instruments, in sidebar order. */
  instrumentIds: number[]
  onRemove: (instrumentId: number) => void
  pollIntervalS: number
  /** The health monitor's expected interval and silent threshold for the
   *  station, which colour each Last Observation (lib/freshness.ts). */
  expectedS?: number
  silentAfterS?: number
  from: Date
  to: Date
  valid: boolean
  /** Where in the side panel the export goes; the page owns its columns. */
  exportSlot: HTMLElement | null
  /** Whether any chart is zoomed inside the time window (its Live badge). */
  onZoomChange?: (zoomed: boolean) => void
  /** The project the page was opened from, which names the export. */
  exportProject?: { name: string; station: string }
}

export function InstrumentPage({ code, instruments, instrumentIds, onRemove,
                                 pollIntervalS, expectedS, silentAfterS, from, to, valid,
                                 exportSlot, onZoomChange, exportProject }: Props) {
  const sensors = useSensorDetails(code, instrumentIds, REFRESH_MS)
  // Use the recorded type from Sensor Info; a hardware model is not a type.
  const catalogue = useAsync(() => api.sensors.list(code), [code])
  const types = new Map((catalogue.data?.sensors ?? [])
    .filter(sensor => sensor.station.code === code && sensor.instrument_id !== null)
    .map(sensor => [sensor.instrument_id!, sensor.sensor_type ?? NO_TYPE]))
  // Channels the reader hid in Sensor Info are left out of the first chart
  // only (lib/hiddenChannels): the tables list them, they can be ticked, and
  // an export has them when ticked.
  const hidden = useHiddenChannels()
  const details = sensors.details
  const hiddenHere = hiddenAt(hidden, code)
  const [selected, setSelected] = useState<string[]>([])

  // Each sensor adds its own default once, when its readings first arrive,
  // not on every refresh: the blocks re-poll every thirty seconds and must
  // not throw away what the reader chose. A new station starts over.
  const seeded = useRef<{ code: string; ids: Set<number> }>({ code, ids: new Set() })
  const loadedKey = details.map((d) => d.instrument_id).join(',')
  useEffect(() => {
    if (seeded.current.code !== code) {
      seeded.current = { code, ids: new Set() }
      setSelected([])
    }
    const fresh = details.filter((d) => !seeded.current.ids.has(d.instrument_id))
    if (!fresh.length) return
    fresh.forEach((d) => seeded.current.ids.add(d.instrument_id))
    const add = defaultSelection(fresh.map((d) => withoutHidden(d, code, hidden)))
    setSelected((current) => [...current, ...add.filter((k) => !current.includes(k))])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, loadedKey])

  const codes = useMemo(() => parametersFor(details, selected), [details, selected])
  const series = useAsync(
    () => codes.length && valid
      ? api.series({ stations: [code], parameters: codes, from, to })
      : Promise.resolve(undefined),
    [code, codes.join(','), from.getTime(), to.getTime(), valid])
  // One list of pins for every chart on the page, for the window and the
  // charted parameters; each chart draws the ones on its own lines.
  const pinning = useChartPins(code, from, to, codes, valid)

  const toggle = (key: string) => setSelected((current) =>
    current.includes(key) ? current.filter((k) => k !== key) : [...current, key])

  if (!instrumentIds.length) {
    return (
      <div className="instrument-empty-state">
        <h2>No Sensors Selected</h2>
        <p className="muted">Tick one or more sensors in the list to chart
          them together and see their latest readings.</p>
      </div>
    )
  }

  const byId = new Map(instruments.map((i) => [i.instrument_id, i]))
  const several = instrumentIds.length > 1
  const charts = series.data
    ? chartGroups(series.data.series, details, selected, several) : []
  const { readings, other } = splitSections(mergeFamilies(details))
  const now = new Date()
  const judge = expectedS && silentAfterS
    ? (at: string | null) => freshness(at, expectedS, silentAfterS, now) : undefined
  const sectionProps = { several, pollIntervalS, selected: new Set(selected),
                         onToggle: toggle, judge, silentAfterS }

  return (
    <div className="instrument-page">
      <SelectionHead ids={instrumentIds} byId={byId} types={types} typeFallback={catalogue.loading ? 'Loading type…' : catalogue.error ? 'Type unavailable' : NO_TYPE}
                     hasData={series.data !== undefined} onRemove={onRemove} />
      {hiddenHere > 0 && <p className="hidden-channels-note" role="status">
        {hiddenHere} {hiddenHere === 1 ? 'channel is' : 'channels are'} left out of the first chart, as you chose in Sensor Info. {hiddenHere === 1 ? 'It is' : 'They are'} still listed and exported.{' '}
        <button type="button" className="link" onClick={() => showAllChannels(code)}>Show all</button></p>}
      {/* Keyed on the codes, not on what was picked: a quantity chosen on a
          sensor since unticked selects nothing, and "No data in this range"
          would blame the range for it. */}
      {!codes.length ? (
        <p className="muted">
          {sensors.loading ? 'Loading…'
            : 'Select a quantity in the readings below to chart it.'}
        </p>
      ) : (
        <>
          <Charts charts={charts} loading={series.loading} error={series.error}
                  truncated={series.data?.truncated ?? false}
                  truncatedAt={series.data?.truncated_at ?? null}
                  from={from} onZoomChange={onZoomChange} pinning={pinning} station={code} />
          {/* In the side panel under the time window, as on the board; the
              columns are this page's, so it is drawn from here. */}
          {exportSlot && createPortal(
            <ExportPanel stations={[code]} parameters={codes}
                         columns={exportColumnsFor(details, codes, code)}
                         from={from} to={to} project={exportProject} />,
            exportSlot)}
        </>
      )}
      <h2 className="readings-head">Readings</h2>
      <SensorStatus ids={instrumentIds} byId={byId} states={sensors.byId} />
      {readings.map((family) => (
        <FamilySection key={family.key} family={family} {...sectionProps} />
      ))}
      <OtherSection families={other} {...sectionProps} />
    </div>
  )
}

/** What the page is of. One sensor is named outright, as its page always
 *  was; several are listed as chips, each of which takes its sensor off the
 *  page, the same action as unticking it in the list. */
function SelectionHead({ ids, byId, types, typeFallback, hasData, onRemove }: {
  ids: number[]; byId: Map<number, Instrument>; types: Map<number, string>; typeFallback: string
  hasData: boolean; onRemove: (id: number) => void
}) {
  // A removed chip takes keyboard focus with it, to <body>. Hand it to the
  // chip that took its place, or to the heading once the chips are gone.
  const head = useRef<HTMLElement>(null)
  const removedAt = useRef<number | null>(null)
  useEffect(() => {
    const at = removedAt.current
    if (at === null || !head.current) return
    removedAt.current = null
    const chips = head.current.querySelectorAll<HTMLButtonElement>('.sensor-chip')
    const target = chips[Math.min(at, chips.length - 1)]
      ?? head.current.querySelector<HTMLElement>('h2')
    target?.focus()
  }, [ids.join(',')])
  const remove = (id: number, index: number) => {
    removedAt.current = index
    onRemove(id)
  }
  const label = (id: number) => {
    const i = byId.get(id)
    return i ? instrumentLabel(i) : String(id)
  }
  if (ids.length === 1) {
    return (
      <header className="content-head instrument-selection-head" ref={head}>
        <div className="instrument-heading-line">
          <h2 tabIndex={-1}>{label(ids[0]!)}</h2>
          <span className="instrument-type">{types.get(ids[0]!) ?? typeFallback}</span>
          {hasData && <span className="badge">Stored readings</span>}
        </div>
      </header>
    )
  }
  return (
    <header className="content-head selection-head" ref={head}>
      <div className="instrument-heading-line"><h2 tabIndex={-1}>{ids.length} Sensors</h2>{hasData && <span className="badge">Stored readings</span>}</div>
      <ul className="sensor-chips" aria-label="Selected sensors">
        {ids.map((id, index) => (
          <li key={id}>
            <button type="button" className="sensor-chip"
                    aria-label={`Remove ${label(id)}`}
                    title={`Remove ${label(id)} from the page`}
                    onClick={() => remove(id, index)}>
              <span>{label(id)}</span>
              <span className="sensor-chip-x" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </header>
  )
}

/** Which ticked sensors are not in the tables below, or are there with
 *  readings that did not refresh. Readings are grouped by type now, so a
 *  sensor's trouble no longer has a block of its own to be shown in: it is
 *  said once, here, by name. */
function SensorStatus({ ids, byId, states }: {
  ids: number[]; byId: Map<number, Instrument>; states: Map<number, SensorState>
}) {
  const name = (id: number) => {
    const i = byId.get(id)
    return i ? instrumentLabel(i) : String(id)
  }
  const loading = ids.filter((id) => !states.has(id))
  const failed = ids.filter((id) => states.get(id)?.error && !states.get(id)?.detail)
  const stale = ids.filter((id) => states.get(id)?.error && states.get(id)?.detail)
  if (!loading.length && !failed.length && !stale.length) return null
  return (
    <>
      {loading.length > 0 && (
        <p className="muted">Loading {loading.map(name).join(', ')}…</p>
      )}
      {failed.map((id) => (
        <p key={id} className="error">{name(id)}: {states.get(id)!.error}</p>
      ))}
      {stale.length > 0 && (
        <p className="muted">
          Could not refresh {stale.map((id) => `${name(id)} (${states.get(id)!.error})`)
            .join(', ')}. Showing the last readings received.
        </p>
      )}
    </>
  )
}

function Charts({ charts, loading, error, truncated, truncatedAt,
                  from, onZoomChange, pinning, station }: {
  charts: ChartGroup[]; loading: boolean; error?: string
  truncated: boolean; truncatedAt: string | null
  from: Date; onZoomChange?: (zoomed: boolean) => void; pinning: Pinning; station: string
}) {
  // One time window over several charts: it is zoomed while any of them is.
  const [zoomed, setZoomed] = useState<Set<string>>(() => new Set())
  const anyZoomed = zoomed.size > 0
  useEffect(() => {
    if (!onZoomChange) return
    onZoomChange(anyZoomed)
    return () => onZoomChange(false)
  }, [anyZoomed, onZoomChange])
  const reportZoom = useCallback((key: string, isZoomed: boolean) => {
    setZoomed((current) => zoomedCharts(current, key, isZoomed))
  }, [])
  return (
    <div className="instrument-charts">
      {truncated && truncatedAt && (
        // Every chart here is cut at the same instant, because one
        // /api/series call carries them all — and exactly where, in the
        // same wording the MDS explorer's own truncation notice uses.
        <p className="notice notice-warn">
          Every chart below stops at the same instant:{' '}
          {truncationMessage(from, truncatedAt)}
        </p>
      )}
      {/* One frame while the query is in flight: no chart of the previous
          selection under the new one's heading. */}
      {loading || error || !charts.length ? (
        <ChartFrame height={320} loading={loading} error={error}
                    empty={!loading && !error && !charts.length}
                    emptyMessage="No data in this range.">
          {null}
        </ChartFrame>
      ) : charts.map((chart) => (
        <ChartFigure key={chart.key} chart={chart} truncated={truncated}
                     onZoomChange={reportZoom} pinning={pinning} station={station} />
      ))}
    </div>
  )
}

/** One chart, with what it says about its own lines beside it. The notes
 *  and the absence notice name lines by their chart labels — Ch1, PZ-01 —
 *  which only mean something beside the chart that drew them: gathered
 *  under every chart, a level Ch1 and a temperature Ch1 read as one. */
function ChartFigure({ chart, truncated, onZoomChange, pinning, station }: {
  chart: ChartGroup; truncated: boolean
  onZoomChange: (key: string, zoomed: boolean) => void
  pinning: Pinning; station: string
}) {
  const nothing = drawsNothing(chart.series)
  const absent = nothing ? null : absentMessage(absentNames(chart.series), truncated)
  const reportZoom = useCallback((zoomed: boolean) => onZoomChange(chart.key, zoomed),
                                 [onZoomChange, chart.key])
  return (
    <figure className="instrument-chart chart-card">
      <figcaption>
        <h3>{chart.heading}</h3>
        {chart.context && <span className="muted">{chart.context}</span>}
      </figcaption>
      {absent && <p className="notice notice-warn">{absent}</p>}
      <ChartFrame height={120} loading={false} empty={nothing} emptyMessage={emptyWindowMessage(chart.series)}>
        <TimeSeriesChart series={chart.series} height={320} onZoomChange={reportZoom}
                         pinning={pinning} pinContext={chart.heading} formatKey={instrumentChartKey(station, chart.key)} />
      </ChartFrame>
      <KindNotes series={chart.series} />
    </figure>
  )
}
